import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

export interface ActivityDayGroup<T> {
  /** `YYYY-MM-DD` bucket key. */
  key: string;
  /** Human label for the day header, e.g. "Jun 24, 2026". */
  label: string;
  entries: T[];
}

/**
 * Buckets activity entries into per-day groups. Input is expected newest-first;
 * output preserves that order (newest day first, newest entry first within a day).
 */
export function groupByDay<T extends { changed_at?: string | null }>(
  entries: T[]
): ActivityDayGroup<T>[] {
  const groups: ActivityDayGroup<T>[] = [];
  for (const entry of entries) {
    // changed_at is a nullable, `any`-typed scalar — guard so a missing/invalid
    // timestamp buckets under "Undated" rather than rendering an "Invalid Date" header.
    const d = entry.changed_at ? dayjs(entry.changed_at) : null;
    const valid = d?.isValid() ?? false;
    const key = valid ? d!.format('YYYY-MM-DD') : 'undated';
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, label: valid ? d!.format('MMM D, YYYY') : 'Undated', entries: [] };
      groups.push(group);
    }
    group.entries.push(entry);
  }
  return groups;
}

/**
 * Inline row time. Same calendar day as `now` → relative ("5 minutes ago"); an
 * earlier day → clock time ("2:15 PM"), since the day header already carries the date.
 */
export function activityRowTime(changedAt?: string | null, now: Date = new Date()): string {
  if (!changedAt) return '';
  const d = dayjs(changedAt);
  return d.isSame(dayjs(now), 'day') ? d.from(dayjs(now)) : d.format('h:mm A');
}

/** Absolute date + time for the row's hover title. */
export function activityFullTime(changedAt?: string | null): string {
  if (!changedAt) return '';
  return dayjs(changedAt).format('MMM D, YYYY, h:mm A');
}
